import { ListStarIcon, ThumbsDownIcon, ThumbsUpIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Popover from '@radix-ui/react-popover'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { match } from 'ts-pattern'
import z from 'zod'
import { useGetScanAndRepository } from '../../hooks/scan-hooks.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { ApiError } from '../../utils/api-client/api-error.ts'
import {
  invalidateGetFixFeedbackSubmissions,
  invalidateGetScaFeedbackSubmissions,
  invalidateGetTriageFeedbackSubmissions,
  useGetFixFeedbackSubmissions,
  useGetProject,
  useGetRepositoryCount,
  useGetScaFeedbackSubmissions,
  useGetTriageFeedbackSubmissions,
  useIsAdmin,
  usePostFixFeedbackSubmission,
  usePostScaFeedbackSubmission,
  usePostTriageFeedbackSubmission,
} from '../../utils/api-client/api-hooks.ts'
import { type FeedbackSubmissionResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './feedback-bar.css.ts'
import { FeedbackForm, type FeedbackScopeContext, useFeedbackForm } from './feedback-form.tsx'
import { FloatingFooter } from './floating-footer.tsx'

// #region Types

export const FeedbackResultTypeSchema = z.enum(['sca', 'triage', 'fix'])
export type FeedbackResultType = z.infer<typeof FeedbackResultTypeSchema>

/**
 * The problem `POST .../feedback-submissions` raises when the repository turned out to be in no
 * project. Keyed on by type rather than on a bare 422, which any other validation failure also uses.
 */
export const REPOSITORY_UNGROUPED_PROBLEM_TYPE = 'pixee:problems/api/feedback-submissions/repository-ungrouped'

export type FeedbackBarProps = {
  resultId: string
  resultType: FeedbackResultType
  /** The scan the result belongs to; it leads to the repository, whose project bounds the feedback's scope. */
  scanId?: string
  drawerContentRef: React.RefObject<HTMLDivElement | null>
}

// #endregion

// #region Hooks

const useFeedback = ({ resultId, resultType }: { resultId: string; resultType: FeedbackResultType }) => {
  const triageQuery = useGetTriageFeedbackSubmissions({ resultId, enabled: resultType === 'triage' })
  const fixQuery = useGetFixFeedbackSubmissions({ resultId, enabled: resultType === 'fix' })
  const scaQuery = useGetScaFeedbackSubmissions({ resultId, enabled: resultType === 'sca' })
  const triageMutation = usePostTriageFeedbackSubmission()
  const fixMutation = usePostFixFeedbackSubmission()
  const scaMutation = usePostScaFeedbackSubmission()
  const queryClient = useQueryClient()

  return match(resultType)
    .with('triage', () => ({
      query: triageQuery,
      mutation: triageMutation,
      invalidate: () => invalidateGetTriageFeedbackSubmissions(queryClient, { resultId }),
    }))
    .with('fix', () => ({
      query: fixQuery,
      mutation: fixMutation,
      invalidate: () => invalidateGetFixFeedbackSubmissions(queryClient, { resultId }),
    }))
    .with('sca', () => ({
      query: scaQuery,
      mutation: scaMutation,
      invalidate: () => invalidateGetScaFeedbackSubmissions(queryClient, { resultId }),
    }))
    .exhaustive()
}

// #endregion

// #region FeedbackBar

/**
 * What the scope selector describes: the repository, its project (with member count) and the estate
 * size. Resolved from the scan lazily; the selector waits for the repository, since without it the
 * choice has nothing to name.
 */
const useFeedbackScopeContext = ({
  scanId,
  enabled,
  isOrganizationOffered,
  isProjectRefused,
}: {
  scanId?: string
  /** The modal being open; nothing is fetched for a bar that is never rated. */
  enabled: boolean
  isOrganizationOffered: boolean
  isProjectRefused: boolean
}): FeedbackScopeContext | undefined => {
  const { repository } = useGetScanAndRepository({
    scanId: enabled && scanId ? scanId : '',
    repositoryEmbed: ['project-membership.project'],
  })
  // The embedded project carries name and member count together, so the scope selector needs no
  // second fetch to describe the project the repository belongs to.
  const project = repository?._embedded?.['project-membership']?._embedded?.project
  const { data: organizationRepositoryCount } = useGetRepositoryCount({ enabled })

  if (!enabled || !scanId || !repository) {
    return undefined
  }
  return {
    repositoryName: repository.full_name,
    project: project ? { name: project.name, repositoryCount: project.repository_count } : undefined,
    organizationRepositoryCount,
    isOrganizationOffered,
    isProjectRefused,
  }
}

export const FeedbackBar: React.FC<FeedbackBarProps> = ({ resultId, resultType, scanId, drawerContentRef }) => {
  const form = useFeedbackForm()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  // Organization is an admin-only scope. `undefined` — the answer is not in yet, or the request for
  // it failed — hides it too: offering a scope the server will refuse is worse than not offering it.
  const isAdmin = useIsAdmin()
  const [isOrganizationRefused, setIsOrganizationRefused] = useState(false)
  const [isProjectRefused, setIsProjectRefused] = useState(false)
  const scopeContext = useFeedbackScopeContext({
    scanId,
    enabled: isModalOpen,
    isOrganizationOffered: isAdmin === true && !isOrganizationRefused,
    isProjectRefused,
  })

  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Memory could not be updated' })

  const {
    query: { data: existingFeedback },
    mutation: { mutate: postSubmission, isPending: isSubmitting },
    invalidate,
  } = useFeedback({ resultId, resultType })
  // Backend enforces at most one submission per result (POST returns 409 on
  // duplicate), so `items` is always 0 or 1 in length.
  const submission = existingFeedback?._embedded?.items?.[0]
  const isSubmitted = !!submission

  const handleBarIconClick = () => {
    if (isSubmitted) {
      setIsPopoverOpen(prev => !prev)
    } else {
      setIsModalOpen(true)
    }
  }

  const handleSubmit = () => {
    const { rating, text, scope } = form.store.state.values
    if (!rating) {
      return
    }
    postSubmission(
      // The scope only travels once the selector was shown; otherwise the API's own default applies.
      { resultId, rating, text, scope: scopeContext ? scope : undefined },
      {
        onSuccess: () => {
          setIsModalOpen(false)
          form.reset()
          addToast({ message: <>Memory successfully updated</>, variant: 'success' })
        },
        onError: (error, ...args) => {
          if ('status' in error && error.status === 409) {
            invalidate()
            setIsModalOpen(false)
            form.reset()
            addToast({ message: <>Feedback was already submitted for this result</>, variant: 'info' })
            return
          }
          // Membership changed under the open modal. The form is kept, Project goes dead, and the
          // scope falls back to the repository so nothing is submitted at a scope that no longer exists.
          if (
            error instanceof ApiError &&
            error.status === 422 &&
            error.problemType === REPOSITORY_UNGROUPED_PROBLEM_TYPE
          ) {
            setIsProjectRefused(true)
            form.setFieldValue('scope', 'repository')
            addToast({
              message: (
                <>
                  This repository isn&apos;t in a project, so the feedback cannot be scoped to one. Choose another
                  scope.
                </>
              ),
              variant: 'error',
            })
            return
          }
          // The admin role went away mid-session. Same shape as above: keep the form, take the scope
          // away, and say so.
          if (error instanceof ApiError && error.status === 403 && scope === 'organization') {
            setIsOrganizationRefused(true)
            form.setFieldValue('scope', 'repository')
            addToast({
              message: <>You can no longer leave organization-wide feedback. Choose another scope.</>,
              variant: 'error',
            })
            return
          }
          showMutationError(error, ...args)
        },
      }
    )
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    form.reset()
    setIsProjectRefused(false)
  }

  return (
    <form.AppForm>
      <FloatingFooter drawerContentRef={drawerContentRef} className={styles.feedbackFooter}>
        <div className={styles.barGroup}>
          <span
            className={isSubmitted ? styles.barLabelRated : styles.barLabel}
            role="button"
            tabIndex={0}
            onClick={handleBarIconClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleBarIconClick()
              }
            }}
          >
            Was this helpful?
          </span>

          {isSubmitted ? (
            <SubmittedBarIcon
              submission={submission}
              isPopoverOpen={isPopoverOpen}
              onIconClick={handleBarIconClick}
              onPopoverClose={() => setIsPopoverOpen(false)}
            />
          ) : (
            <button className={styles.barIconButton} onClick={handleBarIconClick} aria-label="Rate this result">
              <ListStarIcon size={16} />
            </button>
          )}
        </div>
      </FloatingFooter>

      <FeedbackModal
        isOpen={isModalOpen}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onClose={handleModalClose}
        scopeContext={scopeContext}
      />
    </form.AppForm>
  )
}

// #endregion

// #region SubmittedBarIcon

type SubmittedBarIconProps = {
  submission: FeedbackSubmissionResponse | undefined
  isPopoverOpen: boolean
  onIconClick: () => unknown
  onPopoverClose: () => unknown
}

const SubmittedBarIcon: React.FC<SubmittedBarIconProps> = ({
  submission,
  isPopoverOpen,
  onIconClick,
  onPopoverClose,
}) => (
  <Popover.Root
    open={isPopoverOpen}
    onOpenChange={open => {
      if (!open) onPopoverClose()
    }}
  >
    <Popover.Anchor asChild>
      <button
        className={styles.barIconButtonRated}
        onClick={onIconClick}
        aria-label={
          submission?.rating === 'positive' ? 'View submitted positive feedback' : 'View submitted negative feedback'
        }
      >
        {submission?.rating === 'positive' ? (
          <ThumbsUpIcon size={16} weight="fill" />
        ) : (
          <ThumbsDownIcon size={16} weight="fill" />
        )}
      </button>
    </Popover.Anchor>
    <Popover.Portal>
      <Popover.Content className={styles.popoverContent} side="top" align="end" sideOffset={8}>
        <div className={styles.popoverHeader}>
          <p className={styles.popoverMeta}>
            {submission?.createdAt && formatDate({ date: submission.createdAt, type: 'mm/dd/yyyy-hh:mm-ss' })}
            {submission?.username && <span> by @{submission.username}</span>}
          </p>
          <button className={styles.popoverCloseButton} onClick={onPopoverClose} aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>
        {submission && <SubmittedScope submission={submission} />}
        {submission?.text && <p className={styles.popoverText}>{submission.text}</p>}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
)

/**
 * Where the submitted feedback reaches. The scope comes back on the submission itself; a project
 * scope also carries `_links.project`, whose id the member count is read from so the line can say
 * how far "this project" actually goes.
 */
const SubmittedScope: React.FC<{ submission: FeedbackSubmissionResponse }> = ({ submission }) => {
  const projectHref = submission._links?.project?.href
  const projectId = projectHref?.split('?')[0]?.split('/').filter(Boolean).pop()
  const { data: project } = useGetProject({
    projectId: projectId ?? '',
    enabled: submission.scope === 'project' && projectId !== undefined,
  })
  const projectName = project?.name ?? submission._links?.project?.title
  const repositoryCount = project?.repository_count

  const line = match(submission.scope)
    .with('organization', () => <>Applies to your organization</>)
    .with('project', () =>
      projectName === undefined ? (
        <>Applies to this project</>
      ) : repositoryCount === undefined ? (
        <>Applies to {projectName}</>
      ) : (
        <>
          Applies to {projectName} ({repositoryCount} {pluralize(repositoryCount, 'repository', 'repositories')})
        </>
      )
    )
    .with('repository', () => <>Applies to this repository</>)
    .exhaustive()

  return <p className={styles.popoverScope}>{line}</p>
}

// #endregion

// #region FeedbackModal

export type FeedbackModalProps = {
  isOpen: boolean
  isSubmitting: boolean
  onSubmit: () => unknown
  onClose: () => unknown
  scopeContext?: FeedbackScopeContext
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  isSubmitting,
  onSubmit,
  onClose,
  scopeContext,
}) => (
  <Dialog.Root
    open={isOpen}
    onOpenChange={open => {
      if (!open) onClose()
    }}
  >
    <Dialog.Portal>
      <Dialog.Overlay className={styles.modalOverlay} />
      <Dialog.Content className={styles.modalContent} onEscapeKeyDown={onClose} aria-describedby={undefined}>
        <Dialog.Title className={styles.modalTitle}>Was this helpful?</Dialog.Title>
        <p className={styles.modalSubtitle}>What is your overall opinion of this result?</p>
        <FeedbackForm isSubmitting={isSubmitting} onSubmit={onSubmit} onCancel={onClose} scopeContext={scopeContext} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
)

// #endregion
